import { useState, useRef } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Input, Label, Select } from '@/components/ui'
import { Image, Upload, Download, Trash2, Lock, Unlock } from 'lucide-react'
import { toast } from 'sonner'

interface ImageFile {
  id: string
  file: File
  preview: string
  originalWidth: number
  originalHeight: number
  resizedUrl: string | null
}

export function ImageResizer() {
  const [image, setImage] = useState<ImageFile | null>(null)
  const [width, setWidth] = useState(800)
  const [height, setHeight] = useState(600)
  const [lockAspect, setLockAspect] = useState(true)
  const [aspectRatio, setAspectRatio] = useState(1)
  const [format, setFormat] = useState<'jpeg' | 'png' | 'webp'>('jpeg')
  const [quality, setQuality] = useState(90)
  const [processing, setProcessing] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file')
      return
    }

    const preview = URL.createObjectURL(file)
    const img = new window.Image()
    img.onload = () => {
      setImage({
        id: crypto.randomUUID(),
        file,
        preview,
        originalWidth: img.width,
        originalHeight: img.height,
        resizedUrl: null,
      })
      setWidth(img.width)
      setHeight(img.height)
      setAspectRatio(img.width / img.height)
    }
    img.src = preview

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleWidthChange = (newWidth: number) => {
    setWidth(newWidth)
    if (lockAspect) {
      setHeight(Math.round(newWidth / aspectRatio))
    }
  }

  const handleHeightChange = (newHeight: number) => {
    setHeight(newHeight)
    if (lockAspect) {
      setWidth(Math.round(newHeight * aspectRatio))
    }
  }

  const applyPreset = (preset: string) => {
    const presets: Record<string, [number, number]> = {
      'hd': [1280, 720],
      'fullhd': [1920, 1080],
      '4k': [3840, 2160],
      'instagram-square': [1080, 1080],
      'instagram-portrait': [1080, 1350],
      'instagram-landscape': [1080, 566],
      'twitter': [1200, 675],
      'facebook': [1200, 630],
      'linkedin': [1200, 627],
      'youtube': [1280, 720],
    }

    const [w, h] = presets[preset] || [800, 600]
    setWidth(w)
    setHeight(h)
    setLockAspect(false)
  }

  const resizeImage = async () => {
    if (!image) return

    setProcessing(true)

    try {
      const img = new window.Image()
      img.src = image.preview

      await new Promise((resolve) => {
        img.onload = resolve
      })

      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height

      const ctx = canvas.getContext('2d')
      if (!ctx) {
        throw new Error('Failed to get canvas context')
      }

      ctx.drawImage(img, 0, 0, width, height)

      const mimeType = `image/${format}`
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error('Failed to create blob'))),
          mimeType,
          quality / 100
        )
      })

      if (image.resizedUrl) {
        URL.revokeObjectURL(image.resizedUrl)
      }

      const resizedUrl = URL.createObjectURL(blob)
      setImage({ ...image, resizedUrl })
      toast.success('Image resized!')
    } catch (error) {
      toast.error('Failed to resize image')
    } finally {
      setProcessing(false)
    }
  }

  const downloadImage = () => {
    if (!image?.resizedUrl) return

    const link = document.createElement('a')
    link.href = image.resizedUrl
    const ext = format === 'jpeg' ? 'jpg' : format
    link.download = `resized_${width}x${height}.${ext}`
    link.click()
    toast.success('Image downloaded!')
  }

  const clearImage = () => {
    if (image) {
      URL.revokeObjectURL(image.preview)
      if (image.resizedUrl) {
        URL.revokeObjectURL(image.resizedUrl)
      }
    }
    setImage(null)
  }

  const formatSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
            <Image className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Image Resizer</h1>
            <p className="text-muted-foreground">Resize images to specific dimensions</p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[350px_1fr]">
        {/* Settings */}
        <div className="space-y-4">
          <Card className="glass">
            <CardHeader>
              <CardTitle>Settings</CardTitle>
              <CardDescription>Configure resize options</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Upload */}
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-primary transition-colors"
              >
                <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm font-medium">Click to upload image</p>
                <p className="text-xs text-muted-foreground mt-1">PNG, JPG, WebP supported</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>

              {image && (
                <>
                  {/* Original Info */}
                  <div className="p-3 bg-muted/20 rounded-lg text-sm">
                    <p className="text-muted-foreground">Original: {image.originalWidth} × {image.originalHeight}px</p>
                    <p className="text-muted-foreground">Size: {formatSize(image.file.size)}</p>
                  </div>

                  {/* Dimensions */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label>Dimensions</Label>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setLockAspect(!lockAspect)}
                        className="h-8"
                      >
                        {lockAspect ? (
                          <><Lock className="h-4 w-4 mr-1" /> Locked</>
                        ) : (
                          <><Unlock className="h-4 w-4 mr-1" /> Free</>
                        )}
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Width (px)</Label>
                        <Input
                          type="number"
                          value={width}
                          onChange={(e) => handleWidthChange(Number(e.target.value))}
                          min="1"
                          max="10000"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Height (px)</Label>
                        <Input
                          type="number"
                          value={height}
                          onChange={(e) => handleHeightChange(Number(e.target.value))}
                          min="1"
                          max="10000"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Presets */}
                  <div className="space-y-2">
                    <Label className="text-xs">Quick Presets</Label>
                    <div className="flex flex-wrap gap-2">
                      {['HD', 'FullHD', '4K'].map((p) => (
                        <Button
                          key={p}
                          variant="outline"
                          size="sm"
                          onClick={() => applyPreset(p.toLowerCase())}
                          className="text-xs"
                        >
                          {p}
                        </Button>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {['Instagram-Square', 'Twitter', 'Facebook'].map((p) => (
                        <Button
                          key={p}
                          variant="outline"
                          size="sm"
                          onClick={() => applyPreset(p.toLowerCase())}
                          className="text-xs"
                        >
                          {p.replace('-', ' ')}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Format & Quality */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Format</Label>
                      <Select value={format} onChange={(e) => setFormat(e.target.value as 'jpeg' | 'png' | 'webp')}>
                        <option value="jpeg">JPEG</option>
                        <option value="png">PNG</option>
                        <option value="webp">WebP</option>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Quality: {quality}%</Label>
                      <input
                        type="range"
                        min="10"
                        max="100"
                        value={quality}
                        onChange={(e) => setQuality(Number(e.target.value))}
                        className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                      />
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="space-y-2 pt-4">
                    <Button onClick={resizeImage} disabled={processing} className="w-full">
                      {processing ? (
                        <span className="flex items-center gap-2">
                          <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                          Resizing...
                        </span>
                      ) : (
                        'Resize Image'
                      )}
                    </Button>
                    <Button
                      onClick={downloadImage}
                      disabled={!image.resizedUrl}
                      variant="outline"
                      className="w-full"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                    <Button onClick={clearImage} variant="outline" className="w-full">
                      <Trash2 className="h-4 w-4 mr-2" />
                      Clear
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Preview */}
        <Card className="glass">
          <CardHeader>
            <CardTitle>Preview</CardTitle>
            <CardDescription>
              {image ? `${width} × ${height}px` : 'Upload an image to preview'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {image ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Original</p>
                    <div className="border border-border rounded-lg overflow-hidden bg-muted/20">
                      <img
                        src={image.preview}
                        alt="Original"
                        className="w-full h-auto max-h-[400px] object-contain"
                      />
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">
                      {image.resizedUrl ? 'Resized' : 'Preview'}
                    </p>
                    <div className="border border-border rounded-lg overflow-hidden bg-muted/20">
                      <img
                        src={image.resizedUrl || image.preview}
                        alt="Resized"
                        className="w-full h-auto max-h-[400px] object-contain"
                        style={!image.resizedUrl ? { opacity: 0.7 } : undefined}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <Image className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No image uploaded</p>
                <p className="text-xs mt-1">Upload an image to get started</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
